import { type ReactNode } from "react";
import { Link, NavLink, matchPath, useLocation } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import { cn } from "@/lib/utils";
import { MountSinaiLogo } from "./MountSinaiLogo";
import { CommandMenuTrigger } from "./CommandMenu";
import { useOpenCommandMenu } from "./commandMenuContext";

// =============================================================================
// WorkspaceHeader
//
// Full-width app bar for signed-in pages: the Royal × Mount Sinai mark on
// the left, primary navigation beside it, and the account menu on the right.
// One row at every size — below 768px the nav labels collapse to icons (kept
// in the accessibility tree via sr-only), so the sticky height never changes
// and anything sticky below it can use a fixed offset. Nothing in the row
// wraps: the labels carry their own nowrap (not-sr-only resets white-space),
// and when a long name doesn't fit, the trailing slot shrinks and the account
// menu truncates it. Below 390px the spacing and marks tighten, and a touch
// more below 340px, so the row still fits a 320px screen (400% zoom).
//
// An optional second row (`subnav`) carries a section's own tabs, as the
// admin pages do. It isn't pinned with the bar but scrolls away with the
// page, so however many lines its tabs take, what stays on screen is the
// 64px bar. Both are navigation only, so they stay off the page in print.
//
// Introduced with the booking-page redesign; every signed-in page uses it.
// =============================================================================

export interface WorkspaceNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Match only the exact path (for "/"). */
  end?: boolean;
}

export function WorkspaceHeader({
  nav = [],
  subnav,
  trailing,
}: {
  nav?: WorkspaceNavItem[];
  /** A second row under the bar, for a section's own tabs. */
  subnav?: ReactNode;
  trailing?: ReactNode;
}) {
  const { resolved } = useTheme();
  const openCommandMenu = useOpenCommandMenu();
  const { pathname } = useLocation();
  // The tabs show when there's a choice, or when this page isn't one of
  // them (so /account still offers the way back to New trip).
  const showNav =
    nav.length > 1 || nav.some(({ to, end }) => !matchPath({ path: to, end: !!end }, pathname));
  const royalSrc = resolved === "light" ? "/royal-logo-light.png" : "/royal-logo-black.png";
  return (
    <>
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/75 backdrop-blur-xl print:hidden">
        <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-3 px-4 max-[389px]:gap-2 max-[389px]:px-3 max-[339px]:gap-1.5 max-[339px]:px-2.5 sm:gap-6 sm:px-6">
          <Link
            to="/"
            aria-label="Royal Transportation and Mount Sinai Medical Center, home"
            className="flex shrink-0 items-center gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring max-[389px]:gap-1.5 max-[339px]:gap-1 sm:gap-2.5"
          >
            <img src={royalSrc} alt="" className="h-7 w-auto max-[389px]:h-6 sm:h-9" />
            <span aria-hidden="true" className="text-xs font-bold text-muted-foreground sm:text-sm">
              ×
            </span>
            <MountSinaiLogo className="h-5 w-auto text-foreground/90 max-[389px]:h-4 sm:h-7" />
          </Link>
          {showNav ? (
            <nav aria-label="Main" className="flex items-center gap-1 max-[339px]:gap-0.5">
              {nav.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      "inline-flex h-9 items-center gap-2 rounded-lg px-2.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring max-[389px]:px-2 md:px-3",
                      isActive
                        ? "bg-foreground/[0.07] text-foreground"
                        : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground",
                    )
                  }
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only md:not-sr-only md:whitespace-nowrap">{label}</span>
                </NavLink>
              ))}
            </nav>
          ) : null}
          <div className="ml-auto flex min-w-0 items-center gap-2">
            {openCommandMenu ? <CommandMenuTrigger onOpen={openCommandMenu} /> : null}
            {trailing}
          </div>
        </div>
      </header>
      {subnav ? (
        <div className="border-b border-border/60 bg-background/75 print:hidden">
          <div className="mx-auto w-full max-w-[1280px] px-4 max-[389px]:px-3 max-[339px]:px-2.5 sm:px-6">
            {subnav}
          </div>
        </div>
      ) : null}
    </>
  );
}
